import { IonContent, IonPage } from '@ionic/react';

import type { Company } from '~/api/auth';
import { useCompany } from '~/api/hooks/useCompany';
import { AccessLevelTag } from '~/components/AccessLevelTag';
import { CompanySwitcher } from '~/components/CompanySwitcher';
import { Avatar, initialsFrom } from '~/components/ui/Avatar';
import { Icon } from '~/components/ui/Icon';
import { SectionHead } from '~/components/ui/SectionHead';
import { useAppNavigation } from '~/hooks/useAppNavigation';
import { ROUTES } from '~/routes';
import {
  ACCESS_LEVEL,
  COMPANY_ROLE_LABEL,
  FISCAL_MODULES,
  atLeast,
  companyLevel,
} from '~/session/access';

export function Empresa(): JSX.Element {
  const { all: companies, selected, hasMultiple, select } = useCompany();
  const nav = useAppNavigation();

  const heading =
    companies.length === 0
      ? 'Nenhuma empresa'
      : companies.length === 1
        ? 'Sua empresa'
        : 'Suas empresas';

  return (
    <IonPage>
      <IonContent fullscreen>
        <div className="rx-container rx-screen" style={{ paddingBottom: 28 }}>
          <div style={{ padding: '12px 24px 0' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <button
                type="button"
                className="rx-iconbtn"
                onClick={() => nav.back()}
                aria-label="Voltar"
              >
                <Icon name="back" size={18} />
              </button>
              <span
                className="rx-mono"
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.2px',
                  textTransform: 'uppercase',
                  color: 'var(--rx-text-3)',
                }}
              >
                {companies.length.toString().padStart(2, '0')} vinculada
                {companies.length === 1 ? '' : 's'}
              </span>
            </div>

            <h1
              style={{
                margin: '14px 0 0',
                fontSize: 30,
                fontWeight: 500,
                color: 'var(--rx-text)',
                letterSpacing: '-0.7px',
              }}
            >
              {heading}
            </h1>
            <p style={{ marginTop: 4, fontSize: 13, color: 'var(--rx-text-2)' }}>
              {companies.length === 0
                ? 'Solicite ao seu contador o vínculo de uma empresa.'
                : 'Visualização somente leitura. Edições passam pelo contador.'}
            </p>
          </div>

          {companies.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              {hasMultiple && (
                <>
                  <SectionHead label="Operando como" />
                  <div style={{ padding: '0 24px' }}>
                    <CompanySwitcher />
                  </div>
                </>
              )}

              <SectionHead label="Cadastros ativos" />
              <div
                style={{
                  padding: '0 24px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                {companies.map((company) => (
                  <CompanyCard
                    key={company.id}
                    company={company}
                    isSelected={company.id === selected?.id}
                    canSelect={hasMultiple}
                    onSelect={() => select(company.id)}
                    onOpenTeam={() => {
                      select(company.id);
                      nav.goTo(ROUTES.empresaUsuarios);
                    }}
                  />
                ))}
              </div>

              <div style={{ padding: '20px 24px 0' }}>
                <div
                  style={{
                    padding: '10px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    background: 'var(--rx-surface-alt)',
                    borderRadius: 12,
                  }}
                >
                  <Icon name="shield" size={16} color="var(--rx-text-3)" />
                  <p
                    style={{
                      flex: 1,
                      fontSize: 11,
                      color: 'var(--rx-text-2)',
                      lineHeight: 1.45,
                    }}
                  >
                    Dados criptografados ponta-a-ponta. Conforme LGPD. Edições cadastrais passam
                    pelo contador responsável.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}

interface CompanyCardProps {
  company: Company;
  isSelected: boolean;
  canSelect: boolean;
  onSelect(): void;
  onOpenTeam(): void;
}

function CompanyCard({
  company,
  isSelected,
  canSelect,
  onSelect,
  onOpenTeam,
}: CompanyCardProps): JSX.Element {
  const isActive = company.status === 'ACTIVE';

  return (
    <article
      aria-current={isSelected ? 'true' : undefined}
      style={{
        background: 'var(--rx-surface)',
        border: isSelected ? '1px solid var(--rx-brand)' : '0.5px solid var(--rx-border)',
        borderRadius: 18,
        padding: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <Avatar initials={initialsFrom(company.razao_social, 2)} size={48} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 16,
              fontWeight: 500,
              color: 'var(--rx-text)',
              letterSpacing: '-0.3px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {company.razao_social}
          </div>
          {company.nome_fantasia && company.nome_fantasia !== company.razao_social && (
            <div
              style={{
                marginTop: 2,
                fontSize: 12,
                color: 'var(--rx-text-2)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {company.nome_fantasia}
            </div>
          )}
        </div>
        <span
          className={isActive ? 'rx-tag rx-tag-pos' : 'rx-tag'}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {isActive && <Icon name="check" size={10} />}
          {isActive ? 'Ativa' : company.status}
        </span>
      </div>

      <dl
        style={{
          marginTop: 14,
          paddingTop: 12,
          borderTop: '0.5px solid var(--rx-border)',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          margin: 0,
        }}
      >
        <DataRow icon="doc" label="CNPJ" value={company.cpf_cnpj_formatted} mono />
        <DataRow icon="settings" label="ID interno" value={`#${company.id}`} mono />
        <DataRow icon="user" label="Seu papel" value={COMPANY_ROLE_LABEL[company.role]} />
      </dl>

      <div
        style={{
          marginTop: 12,
          paddingTop: 12,
          borderTop: '0.5px solid var(--rx-border)',
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 500,
            color: 'var(--rx-text-3)',
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
          }}
        >
          Seu acesso
        </div>
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {FISCAL_MODULES.map((module) => (
            <AccessLevelTag key={module} module={module} level={company.access[module]} />
          ))}
        </div>
      </div>

      {/* Quem cuida da equipe é quem tem o nível, não quem tem o papel: o nível é a verdade (E9). */}
      {atLeast(companyLevel(company, 'company_users'), ACCESS_LEVEL.full) && (
        <button
          type="button"
          onClick={onOpenTeam}
          style={{
            marginTop: 12,
            width: '100%',
            padding: '10px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: 'var(--rx-surface-alt)',
            border: 'none',
            borderRadius: 12,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          <Icon name="user" size={16} color="var(--rx-text-3)" />
          <span style={{ flex: 1, fontSize: 13, color: 'var(--rx-text)', textAlign: 'left' }}>
            Usuários da empresa
          </span>
          <Icon name="chevron" size={14} color="var(--rx-text-3)" />
        </button>
      )}

      {canSelect && (
        <button
          type="button"
          onClick={onSelect}
          disabled={isSelected}
          style={{
            marginTop: 12,
            width: '100%',
            padding: '10px 12px',
            borderRadius: 12,
            border: isSelected ? 'none' : '0.5px solid var(--rx-border)',
            background: isSelected ? 'var(--rx-brand-tint)' : 'var(--rx-surface)',
            color: isSelected ? 'var(--rx-brand)' : 'var(--rx-text)',
            fontSize: 13,
            fontWeight: 500,
            cursor: isSelected ? 'default' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {isSelected ? 'Empresa em operação' : 'Operar como esta empresa'}
        </button>
      )}
    </article>
  );
}

interface DataRowProps {
  icon: 'doc' | 'settings' | 'mail' | 'user';
  label: string;
  value: string;
  mono?: boolean;
}

function DataRow({ icon, label, value, mono }: DataRowProps): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 11,
          fontWeight: 500,
          color: 'var(--rx-text-3)',
          letterSpacing: '0.4px',
          textTransform: 'uppercase',
        }}
      >
        <Icon name={icon} size={12} />
        {label}
      </span>
      <span
        className={mono ? 'rx-mono' : ''}
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--rx-text)',
          textAlign: 'right',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div style={{ padding: '40px 24px 0', textAlign: 'center' }}>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 52,
          height: 52,
          borderRadius: 26,
          background: 'var(--rx-brand-tint)',
          color: 'var(--rx-brand)',
        }}
      >
        <Icon name="ledger" size={22} />
      </div>
      <p
        style={{
          marginTop: 14,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.6px',
          textTransform: 'uppercase',
          color: 'var(--rx-text-3)',
        }}
      >
        Sem vínculos
      </p>
      <h2
        style={{
          marginTop: 8,
          fontSize: 22,
          fontWeight: 500,
          color: 'var(--rx-text)',
          letterSpacing: '-0.4px',
        }}
      >
        Nada para emitir ainda.
      </h2>
      <p
        style={{
          marginTop: 8,
          fontSize: 13,
          color: 'var(--rx-text-2)',
          maxWidth: '34ch',
          marginInline: 'auto',
        }}
      >
        Peça ao seu contador para vincular uma empresa ao seu acesso.
      </p>
    </div>
  );
}
